<div [class.fullcontainer]='!this.compact' [class.pr-3]='!this.compact' [class.compactAssistant]='this.compact'>
  <div [class.sheet]='!this.compact' [class.compactPanel]='this.compact'>
    <div class='titleContainer'>
      <h1 [class.header]='!this.compact' [class.compactHeader]='this.compact'>Ask AI</h1>
      @if (!this.compact) {
        <div>
          <button class='btn btn-secondary' routerLink='/warehousing/dashboard'>
            <i class="fas fa-tachometer-alt"></i> Dashboard
          </button>&nbsp;
          <button class='btn btn-secondary' routerLink='/warehousing/reports'>
            <i class="fas fa-chart-line"></i> Reports
          </button>
        </div>
      }
    </div>

    <div class='row'>
      <div class='col-md-9'>
        <label for='assistant-question'>Your question</label>
        <textarea id='assistant-question'
          class='form-control'
          rows='3'
          placeholder='e.g. how much of each product is on hand at Bolton?'
          [(ngModel)]='this.question'
          [disabled]='this.streaming'
          (keydown)='this.onQuestionKeydown($event)'></textarea>
        @if (this.questionTooLong) {
          <small class='text-danger'>
            That question is {{ this.question.length }} characters. The limit is
            {{ this.questionMax }} — shorten it, or ask it as two questions.
          </small>
        } @else {
          <small class='text-muted'>Enter to send, shift-Enter for a new line.</small>
        }
      </div>

      <div class='col-md-3'>
        <label for='assistant-provider'>Answered by</label>
        <!-- Locked while an answer is streaming: the turn in flight was sent to one provider
             and its usage is billed to that one, so letting the control move mid-answer would
             label the answer with a model that did not write it. -->
        <select id='assistant-provider'
          class='form-control'
          [(ngModel)]='this.provider'
          [disabled]='this.streaming'>
          @for (item of this.providers; track item.key) {
            <option [value]='item.key'>{{ item.name }}</option>
          }
        </select>
        <small class='text-muted'>Both can propose changes for you to confirm.</small>
      </div>
    </div>

    <br />

    <button class='btn btn-primary' (click)='this.ask()' [disabled]='!this.canAsk'>
      <i class="fas fa-paper-plane"></i> Ask
    </button>&nbsp;

    @if (this.streaming) {
      <button class='btn btn-danger' (click)='this.stop()'>
        <i class="fas fa-stop"></i> Stop
      </button>&nbsp;
    }

    <button class='btn btn-secondary' (click)='this.clear()' [disabled]='this.turns.length === 0'>
      Clear transcript
    </button>

    @if (this.turns.length === 0) {
      <hr />
      <p class='text-muted mb-1'><small>Questions people ask:</small></p>
      @for (example of this.examples; track example) {
        <!-- `btn-secondary` rather than `btn-outline-secondary`: this theme renders the
             outline variant as pale grey on white and the text is close to unreadable. -->
        <button class='btn btn-sm btn-secondary mr-1 mb-1' (click)='this.useExample(example)'>
          {{ example }}
        </button>
      }
    }
  </div>

  @for (turn of this.turns; track this.turnKey($index, turn)) {
    <div [class.sheet]='!this.compact' [class.compactPanel]='this.compact'>
      <p class='assistant-question mb-2'>
        <i class="fas fa-user"></i>&nbsp;
        <strong>{{ turn.question }}</strong>
      </p>

      <!-- Which tools ran, in the order they ran. On a multi-tool question most of the wait
           is these round trips, so without this the screen is blank for long enough to read
           as a hang. Kept after the answer finishes as well: it is the only thing on the
           page that says which data the answer was built from. -->
      @if (turn.toolSteps.length > 0) {
        <p class='assistant-tools text-muted mb-2'>
          <small>
            <i class="fas fa-database"></i>&nbsp;
            Looked up:
            @for (step of turn.toolSteps; track $index) {
              <span class='badge badge-light border ml-1'>{{ step.name }}</span>
            }
          </small>
        </p>
      }

      @if (turn.activeTool !== '') {
        <p class='text-muted mb-2'>
          <i class="fas fa-spinner fa-spin"></i>
          Checking {{ turn.activeTool }}&hellip;
        </p>
      }

      @if (turn.streaming && turn.activeTool === '' && !turn.hasAnswer()) {
        <p class='text-muted mb-2'>
          <i class="fas fa-spinner fa-spin"></i>
          Thinking&hellip;
        </p>
      }

      <!-- Interpolation, NOT innerHTML, and this must stay that way. Tool results carry
           supplier names, location notes and adjustment reasons typed by people, so markup
           can reach here through the model. Rendered like this a script tag is four words
           of text on the screen; bound as HTML it is a script running on a page that is
           logged in. -->
      <!-- One paragraph per run. `.assistant-answer` stays a single containing element so
           anything selecting it still finds the whole answer; the runs are inside it. -->
      @if (turn.hasAnswer()) {
        <div class='assistant-answer'>
          @for (run of turn.answerRuns; track $index) {
            <p class='assistant-run'>{{ run }}</p>
          }
        </div>
      }

      <!-- One card per proposed write. Nothing here has happened - the model only ever gets a
           short "proposed" acknowledgement, never the outcome, so this card and the buttons on
           it are the only place a change is actually confirmed, rejected, applied or fails. -->
      @if (turn.proposals.length > 0) {
        @for (proposal of turn.proposals; track proposal.id) {
          <div class='assistant-proposal mt-2 mb-2'>
            <p class='mb-2'><i class="fas fa-pencil-alt"></i>&nbsp;<strong>{{ proposal.summary }}</strong></p>
            <table class='assistant-proposal-diff mb-2'>
              <tbody>
                @for (entry of proposal.diff; track entry.field) {
                  <tr>
                    <td class='assistant-proposal-field'>{{ entry.field }}</td>
                    <td class='assistant-proposal-old'>{{ entry.oldValue || '(empty)' }}</td>
                    <td><i class="fas fa-arrow-right"></i></td>
                    <td class='assistant-proposal-new'>{{ entry.newValue }}</td>
                  </tr>
                }
              </tbody>
            </table>

            @if (proposal.isPending) {
              <button class='btn btn-success btn-sm' [disabled]='proposal.confirming' (click)='this.confirmProposal(proposal)'>
                <i class="fas fa-check"></i> Confirm
              </button>&nbsp;
              <button class='btn btn-secondary btn-sm' [disabled]='proposal.confirming' (click)='this.rejectProposal(proposal)'>
                <i class="fas fa-times"></i> Reject
              </button>
            }

            @if (proposal.status === 'executed') {
              <span class='text-success'><i class="fas fa-check-circle"></i> Applied</span>
            }
            @if (proposal.status === 'rejected') {
              <span class='text-muted'><i class="fas fa-ban"></i> Rejected</span>
            }
            @if (proposal.status === 'expired') {
              <span class='text-muted'><i class="fas fa-clock"></i> Expired - ask again if you still want this</span>
            }
            @if (proposal.status === 'failed') {
              <span class='text-danger'><i class="fas fa-exclamation-circle"></i> Could not be applied: {{ proposal.errorMessage }}</span>
            }
          </div>
        }
      }

      @if (turn.streaming) {
        <p class='text-muted mt-2 mb-0'><small><i class="fas fa-spinner fa-spin"></i> Answering&hellip;</small></p>
      }

      @if (turn.failed) {
        <!-- The heading states only what is true. With no partial text there is nothing to
             "not finish" — a refusal like a rate limit never started — and the backend's own
             sentence is rendered verbatim either way, never reworded or reclassified. -->
        <div class='alert alert-danger mt-2 mb-0'>
          @if (turn.hasAnswer()) {
            <strong>This answer did not finish.</strong>
          } @else {
            <strong>No answer.</strong>
          }
          {{ turn.errorMessage }}
          @if (turn.hasAnswer()) {
            Do not read what is above as a complete answer.
          }
        </div>
      }

      <!-- Small and at the bottom on purpose: the client wants the running cost visible, not
           competing with the answer. The headline figure is TOTAL input — the backend reports
           the full-rate and cached portions disjointly, so printing `inputTokens` here showed
           the uncached remainder labelled as the total, with a larger "cached" figure beside
           it. The cached share is still called out because it is billed at a fraction of the
           rate, and that difference is the whole cost argument for this feature. -->
      @if (turn.usage) {
        <p class='assistant-usage text-muted mt-2 mb-0'>
          <small>
            {{ this.providerName(turn.provider) }} &middot;
            {{ this.formatTokens(this.totalInput(turn.usage)) }} in
            ({{ this.formatTokens(turn.usage.cachedInputTokens) }} of them cached) &middot;
            {{ this.formatTokens(turn.usage.outputTokens) }} out
          </small>
        </p>
      }
    </div>
  }

  @if (this.hasSessionUsage) {
    <div [class.sheet]='!this.compact' [class.compactPanel]='this.compact'>
      <p class='text-muted mb-0'>
        <small>
          <strong>This session:</strong>
          {{ this.formatTokens(this.totalInput(this.sessionUsage)) }} input tokens
          ({{ this.formatTokens(this.sessionUsage.cachedInputTokens) }} of them served from
          cache, billed at a fraction of the rate) and
          {{ this.formatTokens(this.sessionUsage.outputTokens) }} output tokens.
          Counts reset when the transcript is cleared.
        </small>
      </p>
    </div>
  }
</div>
